import React from 'react';
import PropTypes from 'prop-types';
import { navigate } from '@gatsbyjs/reach-router/';
import { Accordion, AccordionDetails, AccordionSummary } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useGlobal } from '@rcg/ui-react';
import { globalizationPath } from '../../utils/globalization/globalizationPath';
import { ReactComponent as WarningIcon } from '../../assets/svg/warning_outlined.svg';
import Button from '../UiComponents/Button';
import FareAlert from '../FareAlert';
import FlightSummary from '../FligthSummary';
import FlightDetails from '../FlightDetails';
import useDictionary from '../../hooks/useDictionary';
import Dictionary from '../../utils/dictionary';
import { sendTrackingParams } from '../../utils/analytics/browseTracking';
import { setSessionStorageItem } from '../../operations/sessionStorage/utils';
import { getRouteType } from '../../utils/common';
import {
  SESSION_STORAGE_GDS_LOCATOR,
  NAVIGATION_PATHS,
  SESSION_STORAGE_CHANGE_FLIGHTS,
  SESSION_STORAGE_AVAILABLE_TRANSFERS,
  SESSION_STORAGE_FLIGHT_TYPE_CODE,
  FLIGHT_TYPE_CODES,
  SESSION_STORAGE_NAVIGATION_HISTORY_LAST_ENTRY,
} from '../../utils/constants';
import useStyles from './index.style';

const FlightInfoCollapsable = ({
  flightInfo,
  guestNames,
  supportPhoneNumber,
  setOpenCancellationFeesDrawer,
  setSelectedFlightInfo,
}) => {
  const classes = useStyles();
  const {
    data: { lang, market },
  } = useGlobal();
  const getDictionary = useDictionary(Dictionary);

  const urlPath = globalizationPath(market, lang, NAVIGATION_PATHS.base);

  const {
    flightGuest: {
      allowCancellation,
      allowReshop,
      guestFlights,
      gdsLocators,
      transfers,
      refundableFlag,
      airPenaltyAmount,
    },
  } = flightInfo;

  const routeTypeName = {
    roundtrip: getDictionary(Dictionary.airFlightTypeRoundtrip.key),
    oneWay: getDictionary(Dictionary.airFlightTypeOneWay.key),
    multiCity: getDictionary(Dictionary.airFlightTypeMultiCity.key),
  };

  const routeType = getRouteType(guestFlights, routeTypeName);

  const goToCancelFlights = () => {
    setSessionStorageItem(SESSION_STORAGE_GDS_LOCATOR, gdsLocators);
    setSessionStorageItem(SESSION_STORAGE_CHANGE_FLIGHTS, false);
    navigate(`${urlPath}${NAVIGATION_PATHS.flightsCancellation}`);
  };

  const goToChangeFlights = () => {
    setSessionStorageItem(SESSION_STORAGE_CHANGE_FLIGHTS, true);
    setSessionStorageItem(SESSION_STORAGE_GDS_LOCATOR, gdsLocators);
    setSessionStorageItem(SESSION_STORAGE_AVAILABLE_TRANSFERS, transfers.length > 0);
    if (routeType === routeTypeName.oneWay) {
      setSessionStorageItem(SESSION_STORAGE_FLIGHT_TYPE_CODE, FLIGHT_TYPE_CODES.ONE_WAY);
    }
    setSessionStorageItem(SESSION_STORAGE_NAVIGATION_HISTORY_LAST_ENTRY, window.location.href);
    navigate(`${urlPath}${NAVIGATION_PATHS.resultsDeparture}`);
  };

  const openCancellationFees = () => {
    setOpenCancellationFeesDrawer(true);
    setSelectedFlightInfo(flightInfo.flightGuest);
    sendTrackingParams(
      {
        eventType: 'interaction',
        interaction: `air:bookCruise:cancellationFees:clicked`,
      },
      false,
    );
  };
  return (
    <>
      {guestFlights && (
        <>
          <Accordion elevation={0} className={classes.accordion}>
            <AccordionSummary expandIcon={<ExpandMoreIcon className="icon-expanded" />}>
              <FlightSummary guestNames={guestNames} guestFlights={guestFlights} />
            </AccordionSummary>
            <AccordionDetails>
              <div className={classes.detailsWrapper}>
                {guestFlights.map((routeDirection) => (
                  <FlightDetails
                    // Both legs of a round trip share the same gdsLocator (one PNR) — the
                    // direction disambiguates, else React warns about duplicate keys.
                    key={`${routeDirection.gdsLocator}-${routeDirection.routeDirection}`}
                    guestNames={guestNames}
                    route={routeDirection}
                  />
                ))}
              </div>
            </AccordionDetails>
          </Accordion>
          <FareAlert isRefundable={refundableFlag !== false} hasPenalty={airPenaltyAmount > 0} />
          <div className={classes.actions}>
            {allowCancellation && (
              <>
                <Button
                  color="secondary"
                  variant="text"
                  disableRipple
                  className="button-text"
                  onClick={openCancellationFees}>
                  {getDictionary(Dictionary.airCancellationFeesCTA.key)}
                </Button>
                <Button variant="outlined" color="primary" onClick={goToCancelFlights}>
                  {getDictionary(Dictionary.airFlightSummaryCancelFlightsCTA.key)}
                </Button>
              </>
            )}
            {!allowCancellation && (
              <div className="cancel-warning">
                <span>
                  <WarningIcon />
                  {getDictionary(Dictionary.airCancellationNotEligibleText.key)}
                </span>
                <p>
                  {getDictionary(Dictionary.airCancellationAssistanceCallText.key)}
                  <a href={`tel:${supportPhoneNumber}`}>{supportPhoneNumber}</a>
                </p>
              </div>
            )}
            {allowReshop && (
              <Button color="primary" variant="contained" onClick={goToChangeFlights}>
                {getDictionary(Dictionary.airFlightSummaryChangeFlightsCTA.key)}
              </Button>
            )}
          </div>
        </>
      )}
    </>
  );
};
FlightInfoCollapsable.propTypes = {
  flightInfo: PropTypes.shape({
    flightGuest: PropTypes.shape({
      allowCancellation: PropTypes.bool,
      allowReshop: PropTypes.bool,
      guestFlights: PropTypes.arrayOf(
        PropTypes.shape({
          arrivalDate: PropTypes.string,
          confirmationNumbers: PropTypes.arrayOf(PropTypes.string),
          departDate: PropTypes.string,
          flightIcon: PropTypes.string,
          gdsLocator: PropTypes.string,
          numberOfStops: PropTypes.number,
          routeDirection: PropTypes.string,
          totalDurationMins: PropTypes.number,
        }),
      ),
      gdsLocators: PropTypes.arrayOf(PropTypes.string),
      refundableFlag: PropTypes.bool,
      airPenaltyAmount: PropTypes.number,
      transfers: PropTypes.arrayOf(
        PropTypes.shape({
          componentType: PropTypes.string,
          price: PropTypes.number,
          status: PropTypes.string,
          transferCode: PropTypes.string,
          transferDescription: PropTypes.string,
          type: PropTypes.string,
        }),
      ),
    }),
  }),
  guestNames: PropTypes.arrayOf(
    PropTypes.shape({
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
    }),
  ).isRequired,
  supportPhoneNumber: PropTypes.string,
  setOpenCancellationFeesDrawer: PropTypes.func,
  setSelectedFlightInfo: PropTypes.func,
};
export default FlightInfoCollapsable;
